import type { NextApiRequestCookies } from 'next/dist/server/api-utils'
import type { IncomingMessage, ServerResponse } from 'node:http'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'

import { runQuery } from '@/cms/client'
import { navbarQuery, pageQuery } from '@/cms/pages'
import type { Navbar, Page, Route, SiteSettingsNavbar } from '@/cms/pages'
import type { Section } from '@/cms/sections'
import { sectionABTestVariantQuery } from '@/cms/sections'
import { APP_ENV, DOMAIN } from '@/config/env'
import { EVENTS } from '@/constants/analytics'
import { reportError } from '@/services/errorReporting'
import { getSplitClient, getSplitUserKey } from '@/services/splitio'
import { getQueryParams } from '@/utils/url'

export const getNavbarFromSplit = async (
  navbar: SiteSettingsNavbar,
  lang: Language,
  serverContext?: {
    req: IncomingMessage & { cookies: NextApiRequestCookies }
    res: ServerResponse<IncomingMessage>
  }
  // There is actually no problem with this type but Zod has sometimes problems
  // with very deep types
  // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
  // @ts-ignore-next-line
): Promise<Navbar | undefined> => {
  const userKey = getSplitUserKey(serverContext)
  const splitClient = getSplitClient()

  if (navbar._type === 'website.navbar') {
    return await runQuery(navbarQuery, {
      lang,
      id: navbar._id,
    })
  }

  const treatment = splitClient.getTreatment(userKey, navbar.featureFlag, { lang })
  const variation = navbar.variations.find((v) => v.name === treatment)
  if (!variation) {
    reportError(`Assertion: no treatment matched ${treatment} for the experiment ${navbar.title}`)
    return
  }

  await fetch(`${APP_ENV !== 'development' ? 'https://' : 'http://'}${DOMAIN}/api/track`, {
    body: JSON.stringify({
      anonymousId: userKey,
      event: EVENTS.$experiment_started,
      properties: {
        'Experiment name': navbar.featureFlag,
        'Variant name': treatment,
        'Experiment and Variant': `${navbar.featureFlag} - ${treatment}`,
      },
    }),
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })

  return await runQuery(navbarQuery, {
    lang,
    id: variation.navbar._id,
  })
}

export const getPageFromSplit = async (
  // There is actually no problem with this type but Zod has sometimes problems
  // with very deep types
  // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
  // @ts-ignore-next-line
  route: Route,
  lang: Language,
  req: IncomingMessage & { cookies: NextApiRequestCookies },
  res: ServerResponse<IncomingMessage>
) => {
  const userKey = getSplitUserKey({ req, res })
  const splitClient = getSplitClient()
  const requestURL = req.url ?? ''

  const queryParams = getQueryParams(requestURL)

  if (route.page._type === 'website.richContent.page' && '_id' in route.page) {
    return route.page
  } else if (route.page._type === 'website.richContent.page.ab.test' && '_id' in route.page) {
    const treatment = splitClient.getTreatment(userKey, route.page.featureFlag, {
      lang,
      ...queryParams,
    })
    const variation = route.page.variations.find((v) => v.name === treatment)
    if (!variation) {
      reportError(
        `Assertion: no treatment matched ${treatment} for the experiment ${route.page.title}`
      )
      return
    }

    await fetch(`${APP_ENV !== 'development' ? 'https://' : 'http://'}${DOMAIN}/api/track`, {
      body: JSON.stringify({
        anonymousId: userKey,
        event: EVENTS.$experiment_started,
        properties: {
          'Experiment name': route.page.featureFlag,
          'Variant name': variation.name,
          'Experiment and Variant': `${route.page.featureFlag} - ${treatment}`,
        },
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })

    return await runQuery(pageQuery, {
      lang,
      id: variation.page._ref,
    })
  }
  return
}

export const getPageWithSectionsFromSplit = async (
  // There is actually no problem with this type but Zod has sometimes problems
  // with very deep types
  // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error
  // @ts-ignore-next-line
  page: Page,
  lang: Language,
  market: SupportedMarketKey,
  req: IncomingMessage & { cookies: NextApiRequestCookies },
  res: ServerResponse<IncomingMessage>
) => {
  const userKey = getSplitUserKey({ req, res })
  const splitClient = getSplitClient()
  const requestURL = req.url ?? ''

  const queryParams = getQueryParams(requestURL)
  const sections: Exclude<Section, { _type: 'website.richContent.sharedSection.ab.test' }>[] = []
  const url = new URL(requestURL, `https://${DOMAIN}`)
  for (const section of page.sections) {
    if (section._type !== 'website.richContent.sharedSection.ab.test') {
      sections.push(section)
      continue
    }

    const treatment = splitClient.getTreatment(userKey, section.featureFlag, {
      lang,
      asPath: url.pathname,
      ...queryParams,
    })
    const variation = section.variations.find((v) => v.name === treatment)
    if (!variation) {
      reportError(
        `Assertion: no treatment matched ${treatment} for the experiment ${section.title}`
      )
      continue
    }
    await fetch(`${APP_ENV !== 'development' ? 'https://' : 'http://'}${DOMAIN}/api/track`, {
      body: JSON.stringify({
        anonymousId: userKey,
        event: EVENTS.$experiment_started,
        properties: {
          'Experiment name': section.featureFlag,
          'Variant name': variation.name,
          'Experiment and Variant': `${section.featureFlag} - ${treatment}`,
        },
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
    const sectionFromABTest = await runQuery(sectionABTestVariantQuery, {
      lang,
      market,
      id: variation.section._ref,
    })
    // @ts-expect-error
    sections.push(sectionFromABTest)
  }
  return {
    ...page,
    sections,
  }
}
